import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/services/apiService'
import type { PIDControllerState, GCPPoolForecasts, GCPForecastPurpose } from '@/services/api/monitoringApi'

export function usePoolState(poolID: string, refetchInterval?: number | false) {
  return useQuery({
    queryKey: ['pool-state', poolID],
    queryFn: async () => {
      const res = await apiService.monitoring.getPoolState(poolID)
      if (!res.success) throw new Error(res.error ?? 'Failed to fetch pool state')
      return res.data as { battery?: any; ev?: any; meter?: any; pv?: any }
    },
    enabled: !!poolID,
    staleTime: 0,
    refetchInterval,
  })
}

export function usePIDState(poolID: string, refetchInterval?: number | false) {
  return useQuery<PIDControllerState>({
    queryKey: ['pid-state', poolID],
    queryFn: async () => {
      const res = await apiService.monitoring.getPIDState(poolID)
      if (!res.success) {
        const err: any = new Error(res.error ?? 'Failed to fetch PID state')
        err.errorCode = res.errorCode
        throw err
      }
      return res.data!
    },
    enabled: !!poolID,
    staleTime: 0,
    retry: false,
    refetchInterval,
  })
}

export function useFlexBands(poolID: string) {
  return useQuery({
    queryKey: ['flex-bands', poolID],
    queryFn: async () => {
      const res = await apiService.monitoring.getFlexBands(poolID, 48)
      if (!res.success) {
        const err: any = new Error(res.error ?? 'Failed to fetch flex bands')
        err.errorCode = res.errorCode
        throw err
      }
      const data = res.data as any
      return (data?.ev?.flexBand || data?.battery?.flexBand || []) as any[]
    },
    enabled: !!poolID,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

/** Rolling window relative to now */
export type RollingTimeRange = '24h' | '7d' | '30d' | 'yesterday'

/** A specific calendar week identified by ISO week string, e.g. "2026-W03" */
export type WeekTimeRange = { type: 'week'; isoWeek: string }

/** A specific calendar month identified by "YYYY-MM", e.g. "2026-01" */
export type MonthTimeRange = { type: 'month'; month: string }

/** A specific calendar day identified by "YYYY-MM-DD" */
export type DayTimeRange = { type: 'day'; date: string }

export type TimeRange = RollingTimeRange | WeekTimeRange | MonthTimeRange | DayTimeRange

/** Compute the absolute [start, end] ISO strings for any TimeRange value */
export function resolveTimeRange(timeRange: TimeRange): { start: string; end: string } {
  if (typeof timeRange === 'string') {
    if (timeRange === 'yesterday') {
      // Yesterday 00:00:00.000 UTC → 23:59:59.999 UTC
      const now = new Date()
      const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
      const start = new Date(todayUTC - 86_400_000)
      const end = new Date(todayUTC - 1)
      return { start: start.toISOString(), end: end.toISOString() }
    }
    const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 7 * 24 : 30 * 24
    return {
      start: new Date(Date.now() - hours * 60 * 60 * 1000).toISOString(),
      end: new Date().toISOString(),
    }
  }
  if (timeRange.type === 'day') {
    // Interpret the date in local time: midnight → midnight
    const dayStart = new Date(timeRange.date)
    dayStart.setHours(0, 0, 0, 0)
    const dayEnd = new Date(timeRange.date)
    dayEnd.setHours(23, 59, 59, 999)
    return { start: dayStart.toISOString(), end: dayEnd.toISOString() }
  }
  if (timeRange.type === 'month') {
    const [year, month] = timeRange.month.split('-').map(Number)
    const start = new Date(year, month - 1, 1)
    const end = new Date(year, month, 1)
    return { start: start.toISOString(), end: end.toISOString() }
  }
  // ISO week: "YYYY-Www"
  const [yearStr, weekStr] = timeRange.isoWeek.split('-W')
  const year = Number(yearStr)
  const week = Number(weekStr)
  // Find the Monday of that ISO week
  // Jan 4 is always in week 1 of the ISO year
  const jan4 = new Date(year, 0, 4)
  const dayOfWeek = (jan4.getDay() + 6) % 7 // 0=Mon
  const week1Monday = new Date(jan4.getTime() - dayOfWeek * 86400000)
  const weekStart = new Date(week1Monday.getTime() + (week - 1) * 7 * 86400000)
  const weekEnd = new Date(weekStart.getTime() + 7 * 86400000)
  return { start: weekStart.toISOString(), end: weekEnd.toISOString() }
}

export function useChargingSessions(
  poolID: string,
  timeRange: TimeRange,
  enabled = true,
  opts?: { includeMetadata?: boolean }
) {
  const key = typeof timeRange === 'string' ? timeRange : JSON.stringify(timeRange)
  return useQuery({
    queryKey: ['charging-sessions', poolID, key, opts?.includeMetadata ?? false],
    queryFn: async () => {
      const { start, end } = resolveTimeRange(timeRange)
      const include = opts?.includeMetadata ? 'metadata' : undefined
      const res = await apiService.monitoring.getChargingSessions(poolID, { start, end, include })
      if (!res.success) return []
      return (res.data ?? []) as any[]
    },
    enabled:
      enabled &&
      !!poolID &&
      (typeof timeRange !== 'object' || timeRange.type !== 'day' || !!timeRange.date),
  })
}

export function useSystemChargingSessions(systemID: string, timeRange: TimeRange) {
  const key = typeof timeRange === 'string' ? timeRange : JSON.stringify(timeRange)
  return useQuery({
    queryKey: ['system-charging-sessions', systemID, key],
    queryFn: async () => {
      const { start, end } = resolveTimeRange(timeRange)
      const res = await apiService.monitoring.getSystemChargingSessions(systemID, { start, end })
      if (!res.success) throw new Error(res.error ?? 'Failed to fetch system charging sessions')
      return (res.data ?? []) as any[]
    },
    enabled: !!systemID,
  })
}

/**
 * Fetch the latest GCP forecast data for all active GCP purposes on a pool.
 * Returns `GCPPoolForecasts` with one optional entry per purpose.
 * Forecast data is stale after 5 minutes (same cadence as flex bands).
 */
export function usePoolGCPForecasts(poolID: string, purposes?: GCPForecastPurpose[]) {
  return useQuery<GCPPoolForecasts>({
    queryKey: ['gcp-forecasts', poolID, purposes ?? null],
    queryFn: async () => {
      const res = await apiService.monitoring.getPoolGCPForecasts(poolID, purposes)
      if (!res.success) {
        const err: any = new Error(res.error ?? 'Failed to fetch GCP forecasts')
        err.errorCode = res.errorCode
        throw err
      }
      return res.data as GCPPoolForecasts
    },
    enabled: !!poolID,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}
